import { useState } from 'react';
import {
  Stack,
  Group,
  Text,
  Badge,
  Button,
  Card,
  ActionIcon,
  Select,
  Switch,
  Tooltip,
  Avatar,
  Loader,
  Alert,
} from '@mantine/core';
import { IconLink, IconUnlink, IconPlus, IconPhoto, IconAlertCircle } from '@tabler/icons-react';
import { useAssetLinks, useCreateAssetLink, useDeleteAssetLink, useUpdateAssetLink } from '@/hooks/api/useAssetLinks';
import { useAssets } from '@/hooks/api/useAssets';
import type { Asset, AssetLink } from '@/types/models';

interface AssetLinkedAssetsProps {
  asset: Asset;
  organizationId: string | undefined;
}

const statusColors: Record<string, string> = {
  Available: 'green',
  Assigned: 'blue',
  Maintenance: 'yellow',
  Retired: 'gray',
  Lost: 'red',
};

function LinkedAssetCard({
  link,
  linkedAsset,
  isParent,
  onUnlink,
  onToggleGroup,
  isUpdating,
}: {
  link: AssetLink;
  linkedAsset: Asset | undefined;
  isParent: boolean;
  onUnlink: () => void;
  onToggleGroup: (shouldGroup: boolean) => void;
  isUpdating: boolean;
}) {
  if (!linkedAsset) return null;

  return (
    <Card withBorder p="sm">
      <Group justify="space-between" align="flex-start">
        <Group gap="sm">
          {linkedAsset.photo_url ? (
            <Avatar src={linkedAsset.photo_url} size={48} radius="md" />
          ) : (
            <Avatar color="gray" size={48} radius="md">
              <IconPhoto size={24} />
            </Avatar>
          )}
          <div>
            <Group gap="xs">
              <Text size="sm" fw={500}>
                {linkedAsset.description}
              </Text>
              <Badge size="xs" color={isParent ? 'blue' : 'cyan'} variant="light">
                {isParent ? 'Parent' : 'Child'}
              </Badge>
              <Badge size="xs" color={statusColors[linkedAsset.status]} variant="light">
                {linkedAsset.status}
              </Badge>
            </Group>
            <Text size="xs" c="dimmed">
              {linkedAsset.tag_id} - {linkedAsset.brand} {linkedAsset.model}
            </Text>
          </div>
        </Group>
        <Group gap="xs">
          {!isParent && (
            <Tooltip label="When enabled, this child asset will be checked out/in together with the parent">
              <Switch
                size="xs"
                label="Group"
                checked={link.should_group}
                onChange={(e) => onToggleGroup(e.currentTarget.checked)}
                disabled={isUpdating}
              />
            </Tooltip>
          )}
          <Tooltip label="Remove link">
            <ActionIcon color="red" variant="subtle" onClick={onUnlink}>
              <IconUnlink size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>
    </Card>
  );
}

export function AssetLinkedAssets({ asset, organizationId }: AssetLinkedAssetsProps) {
  const [showAddChild, setShowAddChild] = useState(false);
  const [selectedChildId, setSelectedChildId] = useState<string | null>(null);
  const [shouldGroupNew, setShouldGroupNew] = useState(true);

  const { data: links, isLoading } = useAssetLinks(organizationId, asset.id);
  const { data: allAssets = [] } = useAssets(organizationId);
  const createLink = useCreateAssetLink();
  const deleteLink = useDeleteAssetLink();
  const updateLink = useUpdateAssetLink();

  // Filter assets that can be linked as children
  // Exclude: current asset, already linked assets
  const linkedAssetIds = new Set([
    asset.id,
    ...(links?.children.map((l) => l.child_id) || []),
    ...(links?.parents.map((l) => l.parent_id) || []),
  ]);

  const availableAssets = allAssets.filter((a) => !linkedAssetIds.has(a.id));

  const handleAddChild = async () => {
    if (!selectedChildId || !organizationId) return;

    await createLink.mutateAsync({
      organizationId,
      parentId: asset.id,
      childId: selectedChildId,
      shouldGroup: shouldGroupNew,
    });

    setSelectedChildId(null);
    setShowAddChild(false);
    setShouldGroupNew(true);
  };

  const handleUnlink = async (linkId: string) => {
    await deleteLink.mutateAsync(linkId);
  };

  const handleToggleGroup = async (linkId: string, shouldGroup: boolean) => {
    await updateLink.mutateAsync({ id: linkId, shouldGroup });
  };

  if (isLoading) {
    return (
      <Stack align="center" py="xl">
        <Loader size="sm" />
        <Text size="sm" c="dimmed">
          Loading linked assets...
        </Text>
      </Stack>
    );
  }

  const hasParents = links?.parents && links.parents.length > 0;
  const hasChildren = links?.children && links.children.length > 0;

  return (
    <Stack gap="md">
      <Alert icon={<IconAlertCircle size={16} />} color="blue" variant="light">
        <Text size="sm">
          Linked assets allow you to group related items (like a laptop and its accessories).
          When "Group" is enabled, child assets will be automatically checked out/in with the parent.
        </Text>
      </Alert>

      {/* Parent Assets Section */}
      {hasParents && (
        <Stack gap="xs">
          <Text size="sm" fw={600} c="dimmed">
            Parent Assets
          </Text>
          {links?.parents.map((link) => (
            <LinkedAssetCard
              key={link.id}
              link={link}
              linkedAsset={link.parent_asset as Asset | undefined}
              isParent={true}
              onUnlink={() => handleUnlink(link.id)}
              onToggleGroup={(shouldGroup) => handleToggleGroup(link.id, shouldGroup)}
              isUpdating={updateLink.isPending}
            />
          ))}
        </Stack>
      )}

      {/* Child Assets Section */}
      <Stack gap="xs">
        <Group justify="space-between">
          <Text size="sm" fw={600} c="dimmed">
            Child Assets
          </Text>
          {!showAddChild && (
            <Button
              size="xs"
              variant="light"
              leftSection={<IconPlus size={14} />}
              onClick={() => setShowAddChild(true)}
            >
              Link Child Asset
            </Button>
          )}
        </Group>

        {showAddChild && (
          <Card withBorder p="sm" bg="gray.0">
            <Stack gap="sm">
              <Select
                placeholder="Select an asset to link..."
                data={availableAssets.map((a) => ({
                  value: a.id,
                  label: `${a.tag_id} - ${a.description} (${a.brand} ${a.model})`,
                }))}
                value={selectedChildId}
                onChange={setSelectedChildId}
                searchable
                clearable
              />
              <Group justify="space-between">
                <Switch
                  label="Group with parent (checkout together)"
                  checked={shouldGroupNew}
                  onChange={(e) => setShouldGroupNew(e.currentTarget.checked)}
                  size="sm"
                />
                <Group gap="xs">
                  <Button
                    size="xs"
                    variant="subtle"
                    onClick={() => {
                      setShowAddChild(false);
                      setSelectedChildId(null);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="xs"
                    leftSection={<IconLink size={14} />}
                    onClick={handleAddChild}
                    loading={createLink.isPending}
                    disabled={!selectedChildId}
                  >
                    Link Asset
                  </Button>
                </Group>
              </Group>
            </Stack>
          </Card>
        )}

        {hasChildren ? (
          links?.children.map((link) => (
            <LinkedAssetCard
              key={link.id}
              link={link}
              linkedAsset={link.child_asset as Asset | undefined}
              isParent={false}
              onUnlink={() => handleUnlink(link.id)}
              onToggleGroup={(shouldGroup) => handleToggleGroup(link.id, shouldGroup)}
              isUpdating={updateLink.isPending}
            />
          ))
        ) : !showAddChild ? (
          <Text size="sm" c="dimmed" ta="center" py="md">
            No child assets linked. Click "Link Child Asset" to add accessories or related items.
          </Text>
        ) : null}
      </Stack>
    </Stack>
  );
}
